import { Body2 } from '@components/typography/Body2.tsx';
import { Icon } from '@components/Icon.tsx';
import { type IconType } from '@src/IconType.ts';
import { Tooltip } from '../base/Tooltip';
import { classNames } from '@utils/classNames.ts';
import { useMemo, useState } from 'react';

export type MenuItemContentProps = {
  labelId: string;
  isActive: boolean;
  icon?: IconType;
  picture?: string;
  label?: string;
  showLabel?: boolean;
  labelClass?: string;
  isSubMenuItem?: boolean;
};

export const MenuItemContent = ({
  labelId,
  isActive,
  icon,
  picture,
  label,
  showLabel,
  labelClass,
  isSubMenuItem = false,
}: MenuItemContentProps) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const showLabelAsTooltip = useMemo(
    () => label && !showLabel,
    [label, showLabel],
  );

  return (
    <Tooltip
      className="hidden sm:flex bg-slate-700 rounded-lg text-center"
      content={
        <Body2 className="text-white text-sm font-normal">{label}</Body2>
      }
      placement="right"
      handler={
        showLabelAsTooltip ? (open: boolean) => setTooltipOpen(open) : undefined
      }
      open={showLabelAsTooltip ? tooltipOpen : false}
      id={showLabelAsTooltip ? labelId : undefined}
    >
      {!!icon || !!picture ? (
        <div
          className={classNames(
            isActive
              ? 'text-blue-500 sm:text-white sm:bg-blue-500'
              : 'text-slate-900',
            'rounded-xl border-2 border-transparent hover:border-blue-50 flex w-full items-center',
            isSubMenuItem ? 'flex-row' : 'flex-col sm:flex-row',
            showLabel ? 'p-2' : 'p-3.5',
          )}
        >
          {icon && !picture && (
            <Icon
              type={icon}
              className={classNames(
                isActive ? 'bg-white' : 'bg-slate-700',
                'h-5 w-5 flex-shrink-0',
              )}
              aria-hidden="true"
            />
          )}
          {picture && (
            <img
              src={picture}
              alt=""
              className={classNames(
                'bg-white',
                'h-5 w-5 flex-shrink-0 rounded-full',
              )}
            />
          )}
          <Label
            label={label}
            labelId={labelId}
            isActive={isActive}
            showLabel={showLabel}
            labelClass={labelClass}
          />
        </div>
      ) : (
        <></>
      )}
    </Tooltip>
  );
};

const Label = ({
  label,
  labelId,
  isActive,
  showLabel,
  labelClass,
}: Pick<
  MenuItemContentProps,
  'label' | 'showLabel' | 'labelId' | 'isActive' | 'labelClass'
>) =>
  label &&
  showLabel && (
    <Body2
      id={labelId}
      className={classNames(
        isActive ? '!text-blue-500 sm:!text-white' : 'text-slate-900',
        'block text-center px-3 py-1 font-normal text-sm rounded-lg',
        labelClass,
      )}
    >
      {label}
    </Body2>
  );
